import { Button, FormInput } from "@/components/bnp-ui";
import LegalNameNotice from "./LegalNameNotice";
import NameFields from "./NameFields";
import { ACCEPTED_FILE_FORMATS, getDocumentNumberRule } from "./constants";

interface EditDocumentFormProps {
  documentTypeLabel: string;
  editNumber: string;
  onEditNumberChange: (value: string) => void;
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  file: File | null;
  isUploading: boolean;
  onFileChange: (file: File | null) => void;
  onRemoveFile: () => void;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

const EditDocumentForm = ({
  documentTypeLabel,
  editNumber,
  onEditNumberChange,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  file,
  isUploading,
  onFileChange,
  onRemoveFile,
  isPending,
  onCancel,
  onSubmit,
}: EditDocumentFormProps) => {
  const rule = getDocumentNumberRule(documentTypeLabel);
  const isNumberInvalid =
    !!rule &&
    editNumber.trim().length > 0 &&
    !rule.pattern.test(editNumber.trim());

  return (
    <div className="bg-white p-7 rounded-[28px] shadow-[0_10px_35px_rgba(0,0,0,0.03)] border border-gray-200 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-slate-900 font-bold text-base">
            Update {documentTypeLabel}
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Revise identification number or re-upload your document file
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-xl bg-[#F4F6FA] text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors"
        >
          Cancel
        </button>
      </div>

      <LegalNameNotice />

      <div className="space-y-4">
        <NameFields
          firstName={firstName}
          onFirstNameChange={onFirstNameChange}
          lastName={lastName}
          onLastNameChange={onLastNameChange}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            Document ID Number <span className="text-rose-500">*</span>
          </label>
          <FormInput
            type="text"
            name="editNumber"
            value={editNumber}
            placeholder="Enter document identification number"
            className="w-full h-12 rounded-xl bg-[#F8FAFC] border-none px-4 text-sm focus:bg-white focus:ring-2 focus:ring-[#153e46]/20 transition-all"
            onChange={(e) => onEditNumberChange(e.target.value)}
            maxLength={rule?.maxLength}
            inputMode={
              documentTypeLabel.toLowerCase() === "nin" ? "numeric" : "text"
            }
            required
          />
          {rule && (
            <p
              className={`text-[11px] mt-0.5 ${
                isNumberInvalid
                  ? "text-rose-500 font-semibold"
                  : "text-slate-400"
              }`}
            >
              {isNumberInvalid ? rule.error : rule.hint}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            Re-upload document{" "}
            <span className="text-slate-300 font-normal">(Optional)</span>
          </label>
          <div className="flex items-center gap-3">
            <label
              className={`relative flex-1 h-12 flex items-center justify-center gap-2 rounded-xl cursor-pointer transition-all ${
                isUploading
                  ? "bg-[#153e46]/10"
                  : "bg-[#F8FAFC] hover:bg-[#F1F5F9]"
              }`}
            >
              <input
                type="file"
                accept={ACCEPTED_FILE_FORMATS.join(",")}
                onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <span className="text-xs font-semibold text-slate-600">
                {isUploading
                  ? "Uploading replacement..."
                  : file
                    ? file.name
                    : "Choose replacement file"}
              </span>
            </label>
            {file && (
              <button
                type="button"
                onClick={onRemoveFile}
                className="px-4 py-3 rounded-xl bg-rose-50 text-rose-600 text-xs font-semibold hover:bg-rose-100 transition-colors shrink-0"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-100">
        <Button
          className="h-12 px-7 bg-[#153e46] hover:bg-[#0f2e34] text-white text-sm font-semibold rounded-2xl shadow-md shadow-[#153e46]/20 transition-all"
          onClick={onSubmit}
          disabled={
            !editNumber.trim() ||
            isNumberInvalid ||
            !firstName.trim() ||
            !lastName.trim() ||
            isPending
          }
          isLoading={isPending}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default EditDocumentForm;
